import React from "react";
import { Group, Stack, Text, Skeleton, Alert } from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";

import { Display } from "@/utils/api/DisplayService";
import { AppButton, StatusBadge } from "@/components/generic";

export interface DisplayHeaderProps {
  /** The display data to show */
  display: Display | null;

  /** Whether data is loading */
  loading: boolean;

  /** Error message if any */
  error?: string | null;

  /** Additional content to show in the header */
  children?: React.ReactNode;
}

export const DisplayHeader: React.FC<DisplayHeaderProps> = ({
  display,
  loading,
  error,
  children,
}) => {
  const navigate = useNavigate();

  const handleBackToDisplays = () => {
    navigate("/displays");
  };

  if (error) {
    return (
      <Alert
        variant="light"
        color="red"
        title="Error Loading Display"
        icon={<IconInfoCircle size="1rem" />}
      >
        {error}
      </Alert>
    );
  }

  if (loading) {
    return (
      <Stack gap="sm">
        <Group align="flex-start" justify="space-between">
          <Stack gap="xs">
            <Skeleton height={28} width={200} />
            <Skeleton height={20} width={300} />
            <Group gap="sm">
              <Skeleton height={24} width={80} />
              <Skeleton height={24} width={100} />
            </Group>
          </Stack>
          <Group gap="sm">
            <Skeleton height={36} width={120} />
            {children}
          </Group>
        </Group>
      </Stack>
    );
  }

  if (!display) {
    return (
      <Alert
        variant="light"
        color="gray"
        title="Display Not Found"
        icon={<IconInfoCircle size="1rem" />}
      >
        The requested display could not be found.
      </Alert>
    );
  }

  return (
    <Stack gap="sm">
      <Group align="flex-start" justify="space-between">
        <Stack gap="xs">
          <Text size="xl" fw={600}>
            {display.name || "Unnamed Display"}
          </Text>

          {display.description && (
            <Text size="sm" c="dimmed" lineClamp={2}>
              {display.description}
            </Text>
          )}

          <Group gap="sm">
            <StatusBadge status={display.status} />
          </Group>
        </Stack>

        <Group gap="sm">
          <AppButton
            variant="secondary"
            iconName="ArrowLeft"
            onClick={handleBackToDisplays}
          >
            Back to Displays
          </AppButton>
          {children}
        </Group>
      </Group>
    </Stack>
  );
};
